import { TableWrapperStack } from '@common/components';
import { ReadWritePipelineListType } from '@common/queries/pipeline';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Stack, Text, Table, Button, SortConfigType } from '@prophecy/ui-v3';
import React from 'react';

import { useGraphQlQuery } from '../../../data/util';
import { PipelineWithRelation } from '../config/pipeline';
import { SchemaType } from '../config/types';
import { getColumnQuery, getFormattedQuery, getTableCellValue } from '../util';
import { useEntityTableSchema, Error, getEntityTableSortConfigVariables } from './Config';

type Props = {
  uid: string;
  projectId: string;
  entityName: Entity.writeAndReadPipelines;
  sortConfig?: SortConfigType;
};
const RelativeTableUI = ({ entityName, uid, title, url, sortConfig }: Props & { title: string; url: string }) => {
  const entity = Entity.Dataset;
  const entityStr = entityName.replace('writeAndRead', '');
  const schema = useEntityTableSchema(entity, entityName) as SchemaType<PipelineWithRelation>;
  const schemaFieldQuery = getColumnQuery(schema?.columns || []);
  const readEntity = 'read' + entityStr;
  const writeEntity = 'write' + entityStr;
  const childQuery = `
        ${readEntity}{
          ${schemaFieldQuery}
        }
        ${writeEntity}{
          ${schemaFieldQuery}
        }
    `;
  const query = getFormattedQuery(schema, childQuery);
  const variables = { uid };
  const { data, isLoading, isError } = useGraphQlQuery<ReadWritePipelineListType>(
    [schema.childEntity, 'list', variables],
    query,
    undefined,
    variables
  );
  if (isError) {
    return <Error errorMsg={'Some error occurred!'} />;
  }

  const { columns = [] } = schema;
  const currentData = data?.Dataset;
  const filteredColumns = columns.filter((colDef) => colDef.hidden !== true);
  const readData = (currentData?.readPipelines || []).map((rowData) =>
    // @ts-ignore FIXME: fix NestedData
    getTableCellValue(rowData, filteredColumns, 'Read')
  );
  const writeData = (currentData?.writePipelines || []).map((rowData) =>
    // @ts-ignore FIXME: fix NestedData
    getTableCellValue(rowData, filteredColumns, 'Write')
  );
  const rows = [...readData, ...writeData];
  //30 px header height
  return (
    <Stack gap={theme.spaces.x12} grow='1'>
      <Stack direction='horizontal' align='space-between'>
        <Text level='sm'>{title}</Text>
        <Button
          elementType='link'
          to={url}
          variant='secondary'
          size='s'
          icon={<PlusIcon type='default' />}
          aria-label={`Add ${title}`}
          data-test-id='dataset-relations-add-button'
        />
      </Stack>
      <Table
        fixedHeader={true}
        loading={isLoading}
        // @ts-ignore FIXME: fix NestedData
        dataSource={rows}
        columns={filteredColumns}
        width='100%'
        height='100%'
        sortConfig={sortConfig}
      />
    </Stack>
  );
};

export default function DatasetRelations({ uid, projectId }: Pick<Props, 'uid' | 'projectId'>) {
  return (
    <TableWrapperStack height='100%'>
      <RelativeTableUI
        title='Pipelines'
        url={Private_Routes.Create_Entity.tab.getUrl(
          {
            tab: Entity.Pipeline.toLowerCase()
          },
          { projectId }
        )}
        projectId={projectId}
        entityName={Entity.writeAndReadPipelines}
        uid={uid}
        sortConfig={getEntityTableSortConfigVariables(Entity.Pipeline)}
      />
    </TableWrapperStack>
  );
}
